import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import companyService from '../services/company/company.service';
import { CompanySetupStatus, SetupTodoItem } from '../types/company.types';
import { all_routes } from '../router/all_routes';
import { PermissionType } from '../types/user.types';

export const useSetupTodo = () => {
  const [setupStatus, setSetupStatus] = useState<CompanySetupStatus | null>(null);
  const [todoItems, setTodoItems] = useState<SetupTodoItem[]>([]);
  const [loading, setLoading] = useState(true); // Başlangıçta true
  const [initialLoad, setInitialLoad] = useState(true); // İlk yükleme kontrolü
  const [dataReady, setDataReady] = useState(false); // Veri hazır mı kontrolü
  
  const { user } = useSelector((state: RootState) => state.auth);
  const companyId = user?.companyId;
  
  const isAdmin = user?.permissions?.includes(PermissionType.CONTROL_PANEL_ADMIN) || false;

  const createTodoItems = (status: CompanySetupStatus): SetupTodoItem[] => {
    return [
      {
        id: 'branches',
        title: 'Şube Oluştur',
        description: 'İlk şubenizi oluşturarak organizasyonunuzu yapılandırın',
        completed: status.hasBranches,
        route: all_routes.branches,
        icon: 'building',
        priority: 1
      },
      {
        id: 'departments',
        title: 'Departman Oluştur',
        description: 'Departmanlarınızı tanımlayarak çalışanlarınızı gruplandırın',
        completed: status.hasDepartments,
        route: all_routes.departments,
        icon: 'category',
        priority: 2
      },
      {
        id: 'employees',
        title: 'Çalışan Ekle',
        description: 'İlk çalışanınızı ekleyerek sistemi kullanmaya başlayın',
        completed: status.hasEmployees,
        route: all_routes.employeeList,
        icon: 'users',
        priority: 3
      }
    ].sort((a, b) => a.priority - b.priority);
  };

  const fetchSetupStatus = async () => {
    if (!companyId || !isAdmin) {
      setLoading(false);
      setInitialLoad(false);
      setDataReady(true);
      return;
    }
    setLoading(true);
    try {
      const status = await companyService.getCompanySetupStatus(companyId);
      setSetupStatus(status);
      setTodoItems(createTodoItems(status));
      setDataReady(true);
    } catch (error) {
      setDataReady(true);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    if (companyId && isAdmin) {
      fetchSetupStatus();
    } else {
      setLoading(false);
      setInitialLoad(false);
      setDataReady(true);
    }
  }, [companyId, isAdmin]);

  const completedCount = todoItems.filter(item => item.completed).length;
  const totalCount = todoItems.length;
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  
  // Setup complete kontrolü - sadece veri hazırsa kontrol et
  const isSetupComplete = dataReady && setupStatus ? 
    (setupStatus.hasBranches && setupStatus.hasDepartments && setupStatus.hasEmployees) : 
    false;

  return {
    todoItems,
    loading,
    initialLoad,
    dataReady,
    progress,
    completedCount,
    totalCount,
    isSetupComplete,
    setupStatus,
    refreshSetupStatus: fetchSetupStatus
  };
};
